// App-wide authentication + role state. Wraps the router so the login page,
// the route/nav guards and the shell header all share one source of truth.
//
// - `login` posts to loco's `POST /api/auth/login`, stores the JWT and
//   populates the current user (incl. role).
// - `logout` clears the token, drops the WebSocket and wipes the
//   react-query cache so no authenticated data lingers.
// - The token is mirrored from `session.ts`; a 401 anywhere clears the
//   token there, which flips `isAuthenticated` to false here and lets the
//   guard redirect to /login.
// - `ready` is false only while a persisted token's `/current` lookup is in
//   flight on boot, so guards can wait for the role before deciding a
//   landing section (avoids a flash / wrong redirect).

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getJson, sendJson } from '../api/rest';
import { connect as connectWs, disconnect as disconnectWs } from '../ws/connection';
import { clearToken, getToken, onTokenChange, setToken } from './session';
import { usesWebSocket, type Role } from './roles';

export interface AuthUser {
  pid: string;
  name: string;
  email: string;
  role: Role;
}

interface LoginResponse {
  token: string;
  pid: string;
  name: string;
  is_verified: boolean;
  role: Role;
}

interface CurrentResponse {
  pid: string;
  name: string;
  email: string;
  role: Role;
}

interface AuthContextValue {
  user: AuthUser | null;
  role: Role | null;
  isAuthenticated: boolean;
  /** False while a stored token is being validated on boot. */
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  // Ready immediately when there's no token (straight to /login); otherwise
  // wait for the boot `/current` hydrate so role is known before guards run.
  const [ready, setReady] = useState<boolean>(() => !getToken());

  // Keep local state in sync with the session store. A 401 in the fetch
  // layer calls `clearToken()`, which fires here and logs the user out.
  useEffect(
    () =>
      onTokenChange((next) => {
        setTokenState(next);
        if (!next) setUser(null);
      }),
    [],
  );

  // Validate a persisted token on boot and hydrate the current user.
  useEffect(() => {
    if (!getToken()) return;
    getJson<CurrentResponse>('/api/auth/current')
      .then((u) => setUser({ pid: u.pid, name: u.name, email: u.email, role: u.role }))
      .catch(() => {
        // 401 already cleared the token via the fetch layer; nothing to do.
      })
      .finally(() => setReady(true));
  }, []);

  const role = user?.role ?? null;

  // The WebSocket carries feed + grid push and is role-gated server-side, so
  // connect only for roles that use it (everyone except `user`); drop it on
  // logout or for `user`.
  useEffect(() => {
    if (token && usesWebSocket(role)) connectWs();
    else disconnectWs();
  }, [token, role]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      isAuthenticated: !!token,
      ready,
      login: async (email, password) => {
        const res = await sendJson<LoginResponse>('POST', '/api/auth/login', {
          email,
          password,
        });
        setToken(res.token);
        setUser({ pid: res.pid, name: res.name, email, role: res.role });
        setReady(true);
      },
      logout: () => {
        clearToken();
        disconnectWs();
        queryClient.clear();
      },
    }),
    [user, role, token, ready, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
